import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { TableModule } from 'primeng/table';
import { Company, CompanyEnum } from '../../models/company.model';
import { Currency } from '../../models/currency.model';
import { CourseStartDate } from '../../models/pricelist-course-start-date.model';
import { PriceListCourse } from '../../models/pricelist-course.model';
import { PriceListNote } from '../../models/priceslist-note.model';
import { CourseDataTable, CourseTableLabels, Note } from '../../models/types';
import { PriceListCourseBandsService } from '../../services/pricelist-course-bands.service';
import { extractNotes } from '../../utils/pricelist.functions';
import { getBandFromMinMax } from '../../utils/table-utils';
import { CourseStartDatesComponent } from '../course-start-dates/course-start-dates.component';
import { CourseTableComponent } from '../course-table/course-table.component';
import { NotesComponent } from '../notes/notes.component';
import { TableHeaderComponent } from '../table-header/table-header.component';

@Component({
  standalone: true,
  imports: [CommonModule, TableHeaderComponent, CourseStartDatesComponent, NotesComponent, TableModule, CourseTableComponent],
  selector: 'lx-price-lists-portal-adults-course-table',
  templateUrl: './adults-course-table.component.html',
  styleUrls: ['./adults-course-table.component.scss'],
})
export class AdultsCourseTableComponent implements OnInit {
  @Input() courses: PriceListCourse[] = [];
  @Input() courseNotes: PriceListNote[] = [];
  @Input() courseStartDates: CourseStartDate[] = [];
  @Input() currency: Currency | undefined;
  @Input() company: Company = CompanyEnum.KIL;

  dataSource: CourseDataTable[] = [];
  columnLabels: CourseTableLabels | undefined;
  notes: Note[] = [];
  notationMarks: Record<string, string> | undefined;

  private readonly priceListCourseBandsService = inject(PriceListCourseBandsService);

  ngOnInit(): void {
    if (this.courseNotes?.length > 0) {
      const noteCodesInProducts = this.courses
        .flatMap((product) => [...(product.noteCodes ?? [])])
        .filter((noteCode) => noteCode !== null && noteCode !== undefined);
      [this.notes, this.notationMarks] = extractNotes(this.courseNotes, noteCodesInProducts);
    }

    this.dataSource = this.courses.reduce<CourseDataTable[]>((processedCourses, course) => {
      const prices = course.prices.reduce<Record<string, number>>((prices, { price, duration: { min, max } }) => {
        const newPrices = { ...prices };
        newPrices[getBandFromMinMax(min, max)] = price;
        return newPrices;
      }, {});
      return [
        ...processedCourses,
        {
          name: course.name ?? '',
          noteCodes: this.getCleanCourseNoteCodes(course),
          prices,
        },
      ];
    }, []);

    this.columnLabels = this.priceListCourseBandsService.getColumnLabels(this.courses);
  }

  private getCleanCourseNoteCodes(course: PriceListCourse): string[] {
    return (
      course.noteCodes?.filter(
        (noteCode) =>
          // note code is not blank
          noteCode !== null &&
          noteCode !== undefined &&
          // note code is in the list of notation marks
          this.notationMarks &&
          noteCode in this.notationMarks,
      ) ?? []
    );
  }
}
